import { useState, useEffect } from 'react';

/**
 * Hook to detect if the app is running as a PWA
 * Checks multiple indicators to determine PWA status
 */
const usePWAStatus = () => {
  const [isPWA, setIsPWA] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPWAStatus = () => {
      try {
        // Method 1: Check display mode (most reliable)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
        
        // Method 2: Check if running in standalone mode via user agent (iOS Safari)
        const isStandaloneUA = window.navigator.standalone === true;
        
        // Method 3: Check if service worker is active (indicates PWA capability)
        const hasServiceWorker = 'serviceWorker' in navigator;
        
        // Method 4: Check if launched from home screen (iOS Safari)
        const isLaunchedFromHomeScreen = window.navigator.standalone === true;
        
        // Method 5: Check for PWA-specific window features (more conservative)
        const hasPWAFeatures = window.matchMedia('(display-mode: standalone)').matches ||
                              window.navigator.standalone === true;
        
        // Only consider it a PWA if it's actually in standalone mode
        // Don't rely on service worker presence alone as that's available in browsers too
        const pwaDetected = isStandalone || isStandaloneUA || isLaunchedFromHomeScreen;
        
        console.log('🔍 PWA Detection Results:', {
          isStandalone,
          isStandaloneUA,
          hasServiceWorker,
          isLaunchedFromHomeScreen,
          hasPWAFeatures,
          finalResult: pwaDetected,
          userAgent: navigator.userAgent,
          displayMode: window.matchMedia('(display-mode: standalone)').matches
        });
        
        setIsPWA(pwaDetected);
        setIsLoading(false);
      } catch (error) {
        console.error('Error detecting PWA status:', error);
        setIsPWA(false);
        setIsLoading(false);
      }
    };

    // Check immediately
    checkPWAStatus();
    
    // Also listen for display mode changes (in case user switches)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      console.log('🔄 Display mode changed:', e.matches);
      setIsPWA(e.matches);
    };
    
    // Add listener for display mode changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleDisplayModeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleDisplayModeChange);
    }
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleDisplayModeChange);
      } else {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, []);

  return { isPWA, isLoading };
};

export default usePWAStatus;
